import type { RecorderErrorKind } from "@/lib/voice-order";
import type { RecorderStatus } from "@/hooks/useAudioRecorder";
import { Search } from "lucide-react";

import {
  VoiceRecorderChrome,
  type VoiceRecorderChromeCopy
} from "./VoiceRecorderChrome";
import { VoiceRecorderFailureCard } from "./VoiceRecorderFailureCard";

interface VoiceRecorderPanelProps {
  status: RecorderStatus;
  error: RecorderErrorKind | null;
  remainingMs: number;
  onStart: () => void;
  onStop: () => void;
  /** Fall back to the catalog when recording isn't possible. */
  onSearchFallback: () => void;
}

const ERROR_COPY: Record<
  RecorderErrorKind,
  { title: string; body: string; canRetry: boolean }
> = {
  "permission-denied": {
    title: "Microphone access needed",
    body: "We need permission to use your microphone to hear your order. Allow it in your browser, then try again.",
    canRetry: true
  },
  "no-microphone": {
    title: "No microphone found",
    body: "We couldn't find a microphone on this device. Connect one and try again, or search the catalog instead.",
    canRetry: true
  },
  "recording-unavailable": {
    title: "Couldn't start recording",
    body: "Your microphone couldn't be started - it may be in use by another app. Close that and try again.",
    canRetry: true
  },
  unsupported: {
    title: "Voice ordering isn't supported here",
    body: "This browser can't record audio. Try a recent Chrome or Safari, or search the catalog instead.",
    canRetry: false
  },
  unknown: {
    title: "Something went wrong",
    body: "We couldn't start recording. Please try again, or search the catalog instead.",
    canRetry: true
  }
};

/** Order-flow copy for the shared idle/requesting/recording chrome - unchanged
 *  wording from before the KAN-192 extraction. */
const RECORDER_COPY: VoiceRecorderChromeCopy = {
  idleTitle: "Tap and say what you need",
  idleDescription:
    "For example: “two litres of 6% developer and a box of medium foils.”",
  requestingTitle: "Waiting for microphone…",
  requestingDescription: "Allow microphone access to continue.",
  recordingTitle: "Listening…",
  recordingHint: "Tap to finish, or it stops automatically at 60s."
};

/**
 * Stage 0 of the voice flow: capture the spoken order. Renders three mutually
 * exclusive states - a failure card (with a retry and a catalog fallback), the
 * live recording view (pulsing mic, depleting countdown ring, stop button), or
 * the idle prompt to start. The permission-denied failure is called out
 * explicitly because it is the most likely on-stage demo failure.
 *
 * The idle/requesting/recording states are the shared `VoiceRecorderChrome`,
 * and the failure card is the shared `VoiceRecorderFailureCard` (KAN-192) -
 * only this file's `ERROR_COPY` and the catalog fallback are order-specific
 * (see `VoiceMessageRecorderPanel` for the message flow's own copy and
 * "Cancel" fallback).
 */
export function VoiceRecorderPanel({
  status,
  error,
  remainingMs,
  onStart,
  onStop,
  onSearchFallback
}: VoiceRecorderPanelProps) {
  if (error) {
    return (
      <VoiceRecorderFailureCard
        error={error}
        copy={ERROR_COPY[error]}
        onRetry={onStart}
        fallbackLabel="Search the catalog"
        fallbackIcon={Search}
        onFallback={onSearchFallback}
      />
    );
  }

  return (
    <VoiceRecorderChrome
      status={status}
      remainingMs={remainingMs}
      onStart={onStart}
      onStop={onStop}
      copy={RECORDER_COPY}
    />
  );
}
